import { isEqual } from "lodash";
import { BlockRanges, EditedSession, Assignment } from "../context/formData.reducer";
import { Week } from "../context/linkData.reducer";
import {
  sameInstructors,
  sameClassrooms,
  sameIntervals,
  sameBlocks,
} from "./sessionsHaveTheSameResources";
import { Classroom, Instructor } from "@models/ISchema";

type CompareSessionsOutputType = {
  editedSessions: EditedSession;
  assignmentSame: Assignment;
};

const allSequentiallyEqual = <T>(values: T[], compare: (previous: T, current: T) => boolean) => {
  return values.reduce(
    ({ equal, previous }, current) => ({
      equal: !equal || previous == null ? equal : compare(previous, current),
      previous: current,
    }),
    { equal: true, previous: null as T | null },
  ).equal;
};

/**
 * Returns true if all the given sessions have the same `blocks`, false
 * otherwise.
 */
export const sessionsHaveTheSameBlocks = (sessions: EditedSession[]): boolean =>
  allSequentiallyEqual(
    sessions.map(session => session?.blocks ?? ({} as BlockRanges)),
    sameBlocks,
  );

/**
 * Returns true if all the given sessions have the same `days`, false
 * otherwise.
 */
export const sessionsHaveTheSameDay = (sessions: EditedSession[]): boolean =>
  allSequentiallyEqual(
    sessions.map(session => session?.blocks ?? ({} as BlockRanges)),
    (previous, current) => isEqual(previous?.day, current?.day),
  );

export const sessionsHaveTheSameClassrooms = (sessions: EditedSession[]): boolean =>
  allSequentiallyEqual(
    sessions.map(session => session?.classrooms ?? []),
    (previous, current) => sameClassrooms(previous, current as Classroom[]),
  );

/**
 * Returns true if all the given sessions have the same `instructors`, false
 * otherwise.
 */
export const sessionsHaveTheSameInstructors = (sessions: EditedSession[]): boolean =>
  allSequentiallyEqual(
    sessions.map(session => session?.instructors ?? []),
    (previous, current) => sameInstructors(previous, current as Instructor[]),
  );

/**
 * Returns true if all the given sessions have the same checked `intervals`,
 * false otherwise.
 */
export const sessionsHaveTheSameCheckedIntervals = (sessions: EditedSession[]): boolean =>
  allSequentiallyEqual(
    sessions.map(session => session?.intervals ?? []),
    sameIntervals,
  );

/**
 * Given an EditedSession[] array, returns an `Assignment` object checking the
 * equality of each session resource against the others.
 */
export const buildAssignmentSame = (sessions: EditedSession[]): Assignment => ({
  blocks: sessionsHaveTheSameBlocks(sessions),
  day: sessionsHaveTheSameDay(sessions),
  instructors: sessionsHaveTheSameInstructors(sessions),
  classrooms: sessionsHaveTheSameClassrooms(sessions),
  intervals: sessionsHaveTheSameCheckedIntervals(sessions),
});

/**
 * Given an Assignment object, an EditedSession[] array, and a Week[] array,
 * return an EditedSession object.
 */
const buildEditedSessions = (
  assignments: Assignment,
  sessions: EditedSession[],
  linkWeeks: Week[],
): EditedSession => {
  const firstSession = sessions[0];
  const valueOrNull = <T>(value: T): T | null => (sessions.length === 0 ? null : value);

  const newBlocks = valueOrNull(firstSession?.blocks);
  const newDay = valueOrNull(firstSession?.blocks?.day);
  const newSelected = valueOrNull(firstSession?.blocks?.selected);
  const newInstructors = valueOrNull(firstSession?.instructors);
  const newClassrooms = valueOrNull(firstSession?.classrooms);
  const newIntervals = valueOrNull(firstSession?.intervals);

  let editedSessions = {} as EditedSession;

  if (assignments.blocks) {
    editedSessions = {
      ...editedSessions,
      blocks: { ...(newBlocks ?? ({} as BlockRanges)), day: null },
    };
  }

  if (assignments.day) {
    editedSessions = {
      ...editedSessions,
      blocks: {
        ...(editedSessions.blocks ?? ({} as BlockRanges)),
        day: newDay,
        selected: newSelected,
      },
    };
  }

  if (assignments.instructors) {
    editedSessions = { ...editedSessions, instructors: newInstructors };
  }

  if (assignments.classrooms) {
    editedSessions = { ...editedSessions, classrooms: newClassrooms };
  }

  editedSessions = {
    ...editedSessions,
    intervals: assignments.intervals
      ? newIntervals
      : linkWeeks.map(week => ({ ...week, checked: false })),
  };

  return editedSessions;
};

export const compareSessions = (
  sessions: EditedSession[],
  linkWeeks: Week[],
): CompareSessionsOutputType => {
  const assignmentSame = buildAssignmentSame(sessions);
  const editedSessions = buildEditedSessions(assignmentSame, sessions, linkWeeks);
  return {
    editedSessions,
    assignmentSame,
  };
};
